/**
 * Preserving where the operator was heading when their session expired.
 *
 * Every auth failure previously sent them to a bare `/login`, so a deep link —
 * a lead someone shared, a bookmarked appointment — became "log in and then go
 * find it again". These helpers carry the intended path across the login round
 * trip.
 */

const NEXT_PARAM = "next";

/**
 * loginUrlFor builds the login URL, remembering `from` so the operator lands
 * back where they were headed.
 *
 * Same-document paths only. `from` is captured from the browser's own location,
 * but it is round-tripped through a URL the user can edit, so it is re-validated
 * on the way out (see nextTarget) rather than trusted on the way in.
 */
export function loginUrlFor(from?: string | null): string {
  const target = (from ?? "").trim();
  if (!target || !isInternalPath(target) || isLoginPath(target)) {
    return "/login";
  }
  return `/login?${NEXT_PARAM}=${encodeURIComponent(target)}`;
}

/**
 * currentPathForLogin returns the path+query the browser is on, for handing to
 * loginUrlFor. The hash is deliberately dropped: it never reaches the server and
 * is rarely meaningful to restore.
 */
export function currentPathForLogin(): string | null {
  if (typeof window === "undefined") return null;
  return window.location.pathname + window.location.search;
}

/**
 * nextTarget extracts a safe redirect target from the login page's query.
 *
 * Returns null unless the value is a same-origin path. Anything else — an
 * absolute URL, a protocol-relative `//evil.com`, a backslash variant that some
 * parsers normalise to a slash — is rejected. An unchecked `next` here is a
 * textbook open redirect: a link to our own login page that bounces the
 * operator to an attacker's copy of it, with our domain in the address bar
 * right up to the moment they type their password.
 */
export function nextTarget(search: string | URLSearchParams): string | null {
  const params =
    typeof search === "string" ? new URLSearchParams(search) : search;
  const raw = params.get(NEXT_PARAM);
  if (!raw) return null;

  const value = raw.trim();
  if (!isInternalPath(value) || isLoginPath(value)) return null;
  return value;
}

/**
 * isInternalPath accepts only a rooted, same-document path.
 *
 * The checks are deliberately literal rather than delegated to `new URL()`:
 * URL parsing normalises several of these forms into something that looks safe
 * after the fact, which is exactly how open redirects survive review.
 */
function isInternalPath(value: string): boolean {
  if (!value.startsWith("/")) return false;
  // `//host` and `/\host` are protocol-relative — they leave the origin.
  if (value.startsWith("//") || value.startsWith("/\\")) return false;
  // A scheme anywhere in the leading segment means it is not a bare path.
  if (/^\/+[a-z][a-z0-9+.-]*:/i.test(value)) return false;
  // Control characters can truncate the value inside a Location header.
  if (/[ -]/.test(value)) return false;
  return true;
}

/** Bouncing back to the login page would trap the operator in a loop. */
function isLoginPath(value: string): boolean {
  const path = value.split("?")[0];
  return /(^|\/)login(\/|$)/.test(path);
}
